
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEstimates } from '@/hooks/useEstimates';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Layers, FileText, Sparkles } from 'lucide-react';
import EstimateCard from './EstimateCard';
import CreateEstimateDialog from './CreateEstimateDialog';
import { Estimate, EstimateStatus } from '@/types';

type StatusFilter = 'all' | 'draft' | 'sent' | 'won' | 'lost';

/** Mappa lo status sul gruppo del filtro (raccoglie i legacy sotto la nuova etichetta). */
const groupOfStatus = (s: EstimateStatus): StatusFilter => {
  if (s === 'draft') return 'draft';
  if (s === 'sent' || s === 'pending') return 'sent';
  if (s === 'won' || s === 'approved' || s === 'contracted') return 'won';
  if (s === 'lost') return 'lost';
  return 'draft';
};

const FILTER_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'all',   label: 'Tutti' },
  { value: 'draft', label: 'Bozza' },
  { value: 'sent',  label: 'Inviato' },
  { value: 'won',   label: 'Vinto' },
  { value: 'lost',  label: 'Perso' },
];

interface EstimatesListProps {
  projectId: string;
}

const EstimatesList = ({ projectId }: EstimatesListProps) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const navigate = useNavigate();
  
  const { 
    estimates, 
    isLoading, 
    createEstimate, 
    deleteEstimate, 
    updateEstimateStatus,
    isCreating, 
    isDeleting,
    isUpdatingStatus
  } = useEstimates(projectId);

  const handleCreateEstimate = (data: { name: string; description?: string }) => {
    console.log('EstimatesList handleCreateEstimate called with:', data);
    createEstimate({
      ...data,
      projectId,
    });
    setShowCreateDialog(false);
  };

  const handleStatusChange = (estimateId: string, status: Estimate['status']) => {
    console.log('EstimatesList handleStatusChange called with:', { estimateId, status });
    updateEstimateStatus(estimateId, status);
  };

  // CRITICO: useMemo PRIMA dell'early return su isLoading, altrimenti React
  // lancia "Rendered more hooks than during the previous render" quando il
  // primo render avviene con isLoading=true (4 hook chiamati, useMemo skippati)
  // e il secondo con isLoading=false (6 hook chiamati).
  //
  // Conteggio per tab del filtro stato — escludiamo i preventivi locked legacy
  // dai counter "sent"/"won" coerentemente con la mappatura sopra.
  const counts = useMemo(() => {
    const out: Record<StatusFilter, number> = { all: 0, draft: 0, sent: 0, won: 0, lost: 0 };
    for (const e of estimates) {
      out.all++;
      out[groupOfStatus(e.status)]++;
    }
    return out;
  }, [estimates]);

  const filteredEstimates = useMemo(() => {
    if (statusFilter === 'all') return estimates;
    return estimates.filter(e => groupOfStatus(e.status) === statusFilter);
  }, [estimates, statusFilter]);

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Caricamento preventivi...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Preventivi</h2>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Preventivo
        </Button>
      </div>

      {estimates.length > 0 && (
        <div className="flex gap-1 flex-wrap border-b -mb-px">
          {FILTER_TABS.map(tab => {
            const active = statusFilter === tab.value;
            const count = counts[tab.value];
            return (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={
                  'px-3 py-2 text-sm font-medium inline-flex items-center gap-2 border-b-2 transition-colors ' +
                  (active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30')
                }
              >
                {tab.label}
                <Badge
                  variant="secondary"
                  className={'h-5 px-1.5 text-[10px] tabular-nums ' + (active ? 'bg-primary/10 text-primary' : '')}
                >
                  {count}
                </Badge>
              </button>
            );
          })}
        </div>
      )}

      {estimates.length === 0 ? (
        <div className="border border-dashed rounded-lg p-8 bg-gradient-to-br from-background to-primary/5">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary mb-3">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold">Crea il tuo primo preventivo per questo progetto</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              In 3 step generi un preventivo PDF pronto per il cliente. Tutti i prezzi sono calcolati dal tuo catalogo materiali in tempo reale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto mb-6">
            <div className="flex items-start gap-3 p-3 rounded-md bg-background border">
              <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold shrink-0">1</div>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  Crea preventivo
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Dai un nome (es. "Offerta cucina"), il tuo numero offerta è automatico.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md bg-background border">
              <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold shrink-0">2</div>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-primary" />
                  Aggiungi stratigrafie
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Scegli da catalogo certificate o componi dal configuratore, indica m² o pezzi.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md bg-background border">
              <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-bold shrink-0">3</div>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  Esporta PDF
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">Sintetico per il cliente o completo cantiere — con tuo logo e numero offerta.</p>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Button size="lg" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Crea il primo preventivo
            </Button>
          </div>
        </div>
      ) : filteredEstimates.length === 0 ? (
        <div className="text-center py-12 border border-dashed rounded-lg">
          <p className="text-muted-foreground">
            Nessun preventivo con stato <strong>{FILTER_TABS.find(t => t.value === statusFilter)?.label}</strong>.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setStatusFilter('all')}>
            Mostra tutti
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredEstimates.map((estimate) => (
            <EstimateCard
              key={estimate.id}
              estimate={estimate}
              onDelete={deleteEstimate}
              onConfigure={(id) => {
                navigate(`/configurator?estimateId=${id}`);
              }}
              onManage={(id) => {
                navigate(`/estimates/${id}/manage`);
              }}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}

      <CreateEstimateDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreateEstimate={handleCreateEstimate}
        isCreating={isCreating}
      />
    </div>
  );
};

export default EstimatesList;
